// API
import { tmxApiCall } from './index'

// Constants
import { DisclosureType } from '../constants'

export default {
  /**
   * Gets the identity verification details.
   * @param {String|Number} applicationId - The application identifier.
   * @returns {Promise<{approved: Boolean}>} The identity verification details.
   */
  async verifyIdentity (applicationId) {
    return tmxApiCall('get', '/verifyIdentity', {
      params: {
        applicationId
      }
    })
  },

  /**
   * Gets the MLV verification details.
   * @param {String|Number} applicationId - The application identifier.
   * @param {String} productCode - The code identifying the type of loan product being applied for.
   * @returns {Promise<{approved: Boolean}>} The MLV verification details.
   */
  async verifyMlv (applicationId, productCode) {
    return tmxApiCall('get', '/verifyMlv', {
      params: {
        applicationId,
        productCode
      }
    })
  },

  async submitDisclosure (applicationId, disclosureLinks, submittedApplication) {
    return tmxApiCall('post', '/disclosure', {
      applicationId,
      disclosures: disclosureLinks.map(dl => {
        dl.url = dl.linkUrl
        delete dl.linkUrl
        return dl
      }),
      disclosureType: DisclosureType.application,
      submittedApplication
    })
  },

  async applicationStatusUpdate (applicationId) {
    return tmxApiCall('get', '/applicationStatusUpdate', {
      params: {
        applicationId
      }
    })
  },

  async applicationDeclineUpdate (applicationId) {
    return tmxApiCall('get', '/applicationDeclineUpdate', {
      params: {
        applicationId
      }
    })
  },

  /**
   * Validates the geolocation against the application or entered customer address.
   * @param {String} applicationId - The application identifier.
   * @param {String} [customerState] - Optionally, the state associated with the customer's address to compare against.
   * @param {Number} latitude - The user's current latitude.
   * @param {Number} longitude - The user's current longitude.
   * @returns {Promise<{isValid: Boolean, userAddress}>} Whether the location is valid and the geo-coded address.
   */
  async locationValidate (applicationId, customerState, latitude, longitude) {
    return tmxApiCall('get', '/locationValidate', {
      params: {
        applicationId,
        customerState,
        latitude,
        longitude
      }
    })
  },

  /**
   * Sends an SMS to the phone number containing a link to the appraisal mobile app.
   * @param {String} cellPhoneNumber - The cell phone number to send the link to.
   * @param {String} applicationId - The application identifier.
   * @param {Boolean} sendSms - True if an SMS should be sent, false if only the link should be returned (currently an SMS is always being sent).
   * @returns {Promise<{assessmentLink}>} The appraisal mobile app deep link.
   */
  async sendSelfAssessmentText (cellPhoneNumber, applicationId, sendSms) {
    cellPhoneNumber = `+1${cellPhoneNumber}`
    return tmxApiCall('post', '/selfAssessmentText', {
      cellPhoneNumber,
      applicationId,
      sendSms
    })
  },

  async getPrescore (applicationId, vehicleEstimate) {
    return tmxApiCall('post', '/prescore', {
      applicationId,
      vehicleEstimate
    })
  },

  async saveLoanProduct (applicationId, loanProduct, custLoanTypeSelection) {
    return tmxApiCall('put', '/loan', {
      applicationId,
      loanProduct,
      custLoanTypeSelection
    })
  },

  async getScanIdDataUuid (cellPhoneNumber) {
    return tmxApiCall('get', '/scanIdLink', {
      params: {
        cellPhoneNumber
      }
    })
  },

  async sendScanIdTextMessage (cellPhoneNumber, scanIdDataUuid) {
    return tmxApiCall('put', '/scanIdLink', {
      cellPhoneNumber,
      scanIdDataUuid
    })
  },

  async getScanIdData (scanId) {
    return tmxApiCall('get', '/scanIdData', {
      params: {
        scanId
      }
    })
  },

  async saveScanIdData (scanId, data) {
    return tmxApiCall('post', '/scanIdData', {
      scanId,
      data
    })
  },

  async getSignatureUrl (applicationId) {
    return tmxApiCall('post', '/signatureAuthUrl', {
      applicationId
    })
  },

  async validateEmail (email, applicationId) {
    return tmxApiCall('post', '/validateEmail', {
      applicationId,
      email
    })
  }
}
